import {getHasSystemMetrics} from '../../../../PanelRunsLinePlot/common';
import {RunsLinePlotConfig} from '../../../../PanelRunsLinePlot/types';
import {getCascadingSetting} from '../../getCascadingSetting';
import {LinePlotSettings, XAxisValues} from '../../types';
import {isSettingActive} from '../../utils';
import {DEFAULT_X_AXIS_SETTINGS} from '../linePlotDefaults';

export const getCascadingXAxisSettings = (
  parentSettings: LinePlotSettings | undefined,
  childSettings?: LinePlotSettings,
  panelSettings?: RunsLinePlotConfig
): {
  xAxis: XAxisValues | string;
  xAxisMin: number | undefined;
  xAxisMax: number | undefined;
  xLogScale: boolean | undefined;
} => {
  const xAxis = getXAxis(parentSettings, childSettings, panelSettings);

  return {
    xAxis,
    xAxisMin: getXAxisRangeValue(
      'xAxisMin',
      xAxis,
      parentSettings,
      childSettings,
      panelSettings
    ),
    xAxisMax: getXAxisRangeValue(
      'xAxisMax',
      xAxis,
      parentSettings,
      childSettings,
      panelSettings
    ),
    xLogScale: getCascadingSetting([
      parentSettings?.xLogScale,
      childSettings?.xLogScale,
      panelSettings?.xLogScale,
    ]),
  };
};

const getXAxis = (
  parentSettings: LinePlotSettings | undefined,
  childSettings?: LinePlotSettings,
  panelSettings?: RunsLinePlotConfig
) => {
  const isSystemMetricPanel =
    panelSettings != null && getHasSystemMetrics(panelSettings);
  const xAxis = getCascadingSetting([
    parentSettings?.xAxis,
    childSettings?.xAxis,
  ]);

  if (panelSettings?.xAxis != null) {
    return panelSettings?.xAxis;
  }

  if (isSystemMetricPanel) {
    // the panel doesn't have its own xAxis, but it can't use the global
    // xAxis because it isn't compatible with system metrics -- fall back
    // to '_runtime'
    if (['_runtime', '_timestamp'].indexOf(xAxis) === -1) {
      return '_runtime';
    }
    return xAxis;
  }

  if (
    panelSettings?.startingXAxis != null &&
    childSettings?.xAxis == null &&
    parentSettings?.xAxis == null
  ) {
    // If the global xAxis settings are not active, use default x-axis.
    // This startingXAxis value can be populated if user calls wandb.define_metric()
    // and specifies the step_metric arg. This arg is used to specify which x-axis should
    // be used for auto-generated charts. Look at sdk code for further details
    // https://github.com/wandb/wandb/blob/fa4a7e4374fd4a0048548a483516f98482f5fdc3/wandb/sdk/wandb_run.py#L2736-L2737
    return panelSettings?.startingXAxis;
  }

  if (childSettings?.xAxis != null) {
    return childSettings?.xAxis;
  }

  return xAxis;
};

const getXAxisRangeValue = (
  field: 'xAxisMin' | 'xAxisMax',
  globalXAxis: XAxisValues | string | undefined,
  parentSettings: LinePlotSettings | undefined,
  childSettings?: LinePlotSettings,
  panelSettings?: RunsLinePlotConfig
) => {
  if (panelSettings != null && panelSettings[field] != null) {
    return panelSettings[field];
  }

  // Note: we treat default setting as if user hasn't modified setting yet
  if (
    panelSettings == null ||
    !isSettingActive(panelSettings?.xAxis, DEFAULT_X_AXIS_SETTINGS.xAxis) ||
    globalXAxis === panelSettings?.xAxis
  ) {
    // we can only use the global value if the global xAxis matches the
    // one we're actually using for this panel
    return getCascadingSetting(
      [parentSettings, childSettings].map(item =>
        item != null && field in item ? item[field] : undefined
      )
    );
  }

  return undefined;
};
